import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import SlotBadge from '@/components/PickupSlots/SlotBadge';
import { useRespondPickupSlot } from '@/hooksApi';
import { PickupItem } from '@/types';
import useChangeSlotStore from '@/stores/useChangeSlotStore';
import useRolloverToastStore from '@/stores/useRolloverToastStore';
import useDeferConfirmStore from '@/stores/useDeferConfirmStore';
import { isSlotEditable } from './slotEditability';
import { formatSlotWindow12 } from '@/utils/helpers';

interface PickupSlotCellProps {
  uid: string;
  slot?: PickupItem['slot'];
  slotState?: string | null;
  status?: string;
  isNextDay?: boolean;
  slotResponseOpen?: boolean;
}

// v2-task-8b (§2.1/§2.4): h-6/24px sat below any comfortable tap target and
// rounded-md (6px) was a one-off radius on its own scale step — h-8 (32px,
// the "compact" tier) + rounded-lg (8px, radius-sm) bring these in line with
// every other small control in the feature, plus the unified
// disabled-opacity/focus-visible/active-press states (§2.6).
const actionBtnClass =
  'h-8 px-3 rounded-lg text-[11px] font-semibold whitespace-nowrap transition-colors cursor-pointer active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-45 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[rgb(237,224,74)]';

// Slot column cell for the pickups list: renders the current slot badge, the
// "your pickup will be tomorrow" banner with a clickable trailing sentence,
// and — when the backend is waiting on a rollover response — the
// Accept / Change / Defer actions. All "change slot" flows open the SINGLE
// page-level ChangeSlotModal via useChangeSlotStore (no per-row modal: see
// the QA pointer-events bug note in ChangeSlotModal). Resolving a rollover
// from here also dismisses the RolloverToast for the same pickup (both
// surfaces act on the same underlying response), and a successful Defer
// opens the shared DeferConfirmModal, matching the toast's Defer flow.
const PickupSlotCell = ({
  uid,
  slot,
  slotState,
  status,
  isNextDay,
  slotResponseOpen,
}: PickupSlotCellProps) => {
  const { t } = useTranslation();
  const openChangeSlot = useChangeSlotStore((state) => state.openChangeSlot);
  const dismissRolloverToast = useRolloverToastStore((state) => state.dismiss);
  const openDeferConfirm = useDeferConfirmStore((state) => state.openDeferConfirm);
  const { mutate: respondSlot, isLoading: isResponding } = useRespondPickupSlot();

  // NOTE: this row must NEVER predict where the pickup will land — it already
  // landed. CurrentSlotBanner's "next slot" notice is forward-looking (what a
  // NEW shipment would do right now) and its prediction is legitimate there;
  // mirroring it here was not. The backend resolves the slot ONCE at ship time
  // (RequestJob::applySlotPlan): auto-select finding a same-day slot yields
  // slot_assigned on TODAY's operational_date, so `is_next_day` is false and
  // this whole block never renders. Reaching here at all means the server took
  // the next_day_fallback branch — the pickup IS tomorrow. Re-deriving the
  // answer from the live slots feed contradicted it, because a slot still open
  // *now* says nothing about a pickup shipped before an earlier cutoff.
  // Reported on BXPU-262098770901: row read "scheduled to the next slot" while
  // the slot-demand feed had it on operational_date 2026-07-30, next_day_fallback.

  // Same predicate as the row-dropdown `change_time_slot` item (list.tsx) —
  // when the slot is locked (route_included/route_appended) or the pickup is
  // terminal, the banner still shows the full verbatim copy but its trailing
  // sentence is NOT clickable.
  const canEditSlot = isSlotEditable(slotState, status);

  // Both EN and AR copy are two sentences joined by ". " — the second
  // sentence ("If you want to change it, change the time slot for this
  // pickup.") is the clickable part; the concatenation of the two pieces
  // must remain the exact verbatim string from the translation file.
  const bannerText = String(t('pickup_tomorrow_banner'));
  const splitIndex = bannerText.indexOf('. ');
  const leadSentence = splitIndex >= 0 ? bannerText.slice(0, splitIndex + 1) : bannerText;
  const trailingSentence = splitIndex >= 0 ? bannerText.slice(splitIndex + 2) : '';

  const slotWindow = formatSlotWindow12(slot?.window_from, slot?.window_to);

  const handleRespond = (choice: 'accept' | 'defer') => {
    respondSlot(
      { uid, choice },
      {
        onSuccess: (res) => {
          message.success(String(t('operation')));
          dismissRolloverToast(uid);
          if (choice === 'defer') {
            // A defer rebooks the pickup onto its ORIGINAL slot on the next
            // operational day — surface THAT landing slot (resulting_slot),
            // falling back to the local slot values only when the API omits it.
            const resultingSlot = res?.resulting_slot;
            openDeferConfirm({
              label: resultingSlot?.label ?? slot?.label ?? '',
              window: resultingSlot
                ? formatSlotWindow12(resultingSlot.window_from, resultingSlot.window_to)
                : slotWindow,
            });
          }
        },
      },
    );
  };

  return (
    <div className='flex flex-col gap-1 items-start'>
      <SlotBadge slot={slot} slotState={slotState} showIcon />
      {/* §6.3 — compact per-pickup preferred-timing sub-line under the slot
          badge. Always shown for a real slot (Anytime is a valid state); the
          best-effort caption is added on its own line (this compact cell is the
          "mobile" variant) only once a bucket is set. */}
      {slot?.code ? (
        <div className='flex flex-col items-start'>
          <span className='text-[11px] leading-tight text-[rgb(115,123,140)]'>
            {String(t('preferred_timing_label'))}:{' '}
            <span className='font-semibold text-[rgb(57,57,60)]'>
              {String(t(slot.preferred_timing == null ? 'anytime' : slot.preferred_timing))}
            </span>
          </span>
          {slot.preferred_timing != null ? (
            <span className='mt-0.5 text-[10px] leading-snug text-[rgb(115,123,140)]'>
              {String(t('preferred_timing_best_effort_line'))}
            </span>
          ) : null}
        </div>
      ) : null}
      {/* Bug TC08: the "your pickup is tomorrow — change the time slot" banner
          references a slot, so it must only show for a real platform slot. A
          legacy pickup (slot null / no code) reads cleanly as "No time slot
          selected" via SlotBadge alone, with no contradictory next-day copy. */}
      {slot?.code && isNextDay ? (
        <div className='max-w-[230px] text-[11px] leading-snug text-amber-700'>
          {canEditSlot && trailingSentence ? (
            <>
              {leadSentence}{' '}
              <button
                type='button'
                className='inline p-0 border-0 bg-transparent text-inherit underline font-medium cursor-pointer hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[rgb(237,224,74)]'
                onClick={(event) => {
                  event.stopPropagation();
                  openChangeSlot({
                    uid,
                    currentCode: slot?.code,
                    currentPreferredTiming: slot?.preferred_timing,
                    mode: 'edit',
                  });
                }}
              >
                {trailingSentence}
              </button>
            </>
          ) : (
            bannerText
          )}
        </div>
      ) : null}
      {slotResponseOpen ? (
        <div className='flex items-center gap-2 mt-0.5'>
          <button
            type='button'
            disabled={isResponding}
            className={`${actionBtnClass} bg-[rgb(237,224,74)] text-[rgb(57,57,60)] hover:bg-[rgb(230,217,60)]`}
            onClick={(event) => {
              event.stopPropagation();
              handleRespond('accept');
            }}
          >
            {String(t('slot_accept'))}
          </button>
          <button
            type='button'
            disabled={isResponding}
            // v2-task-12fix (H1): `border` alone only carries border-WIDTH
            // with Preflight off — `border-solid` restores the visible outline.
            className={`${actionBtnClass} border border-solid hover:bg-[rgb(245,245,243)]`}
            style={{ borderColor: 'rgb(232,230,227)' }}
            onClick={(event) => {
              event.stopPropagation();
              dismissRolloverToast(uid);
              openChangeSlot({ uid, currentCode: slot?.code, mode: 'respond' });
            }}
          >
            {String(t('slot_change'))}
          </button>
          <button
            type='button'
            disabled={isResponding}
            className={`${actionBtnClass} border border-solid text-[rgb(115,123,140)] hover:bg-[rgb(245,245,243)]`}
            style={{ borderColor: 'rgb(232,230,227)' }}
            onClick={(event) => {
              event.stopPropagation();
              handleRespond('defer');
            }}
          >
            {String(t('slot_defer'))}
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default PickupSlotCell;
